import { ProjectPropertyType } from 'src/constants/project';
import { ModelDataHighlight } from 'src/containers/ProjectDiagram/constants/highlight';
import {
  FileUpload,
  Mapping,
  MappingLabel,
  ModelConceptAttribute,
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
  ModelRule,
  RelationshipMapping,
  Resource,
  VirtualGraph,
} from 'src/containers/ProjectDiagram/types/data';

export type ModelDataHighlighted = {
  key: string;
  type: ModelDataHighlight;
};

export interface BaseModelData extends go.ObjectData {
  flaggedForRedraw?: boolean;

  highlighted: ModelDataHighlighted | null;
  selection: string[];

  mappingIdx: number;

  attributesByIri: Record<string, ModelConceptAttribute>;
  colorIdsByIri: Record<string, string>;
  descriptionsByIri: Record<string, string>;
  labelsByIri: Record<string, string>;
  propertyTypesByIri: Record<string, ProjectPropertyType>;
  rulesById: Record<string, ModelRule>;
  sensitivePropertyGroupsByName: Record<string, string[]>;
}

export interface ModelData extends BaseModelData {
  draftData?: {
    prevNodeDataArray: NodeData[];
    prevLinkDataArray: LinkData[];
    prevModelData: BaseModelData;
    originalSpgNamesByCurrentName: Record<string, string>;
  };
}

export const createEmptyModelData = (): ModelData => ({
  attributesByIri: {},
  colorIdsByIri: {},
  descriptionsByIri: {},
  highlighted: null,
  labelsByIri: {},
  mappingIdx: 0,
  propertyTypesByIri: {},
  rulesById: {},
  selection: [],
  sensitivePropertyGroupsByName: {},
});

export type NodeData =
  | FileUpload
  | VirtualGraph
  | Resource
  | MappingLabel
  | ModelConceptClass
  | ModelConceptHierarchyLabel
  | ModelConceptRelationshipLabel;

export type LinkData =
  | Mapping
  | ModelConceptHierarchy
  | ModelConceptRelationship
  | RelationshipMapping;

export interface Model extends go.GraphLinksModel {
  modelData: ModelData;

  nodeDataArray: NodeData[];
  linkDataArray: LinkData[];

  commit(func: (m: Model) => void, tname?: string | null): void;

  /**
   * Given a number or string, find the node data object in this model
   * that uses the given value as its unique key.
   *
   * **Do not use this method to find data within a transaction or commit.
   * Its return value is determined from the original state at the beginning
   * of the transaction/commit instead of the "current" intermediate state.**
   *
   * @param {(string|number|undefined)} key a string or a number.
   * @return {Object} null if the key is not present in the model,
   * or if the key is null or undefined or not a string or number.
   * @see #containsNodeData
   * @see #getKeyForNodeData
   */
  findNodeDataForKey(key: go.Key): NodeData | null;

  /**
   * Given a number or string, find the link data object in this model
   * that uses the given value as its unique key.
   *
   * Unless #linkKeyProperty is set to a non-empty string, this model
   * will not automatically assign unique key values for link data objects,
   * and thus this method will always return null.
   *
   * **Do not use this method to find data within a transaction or commit.
   * Its return value is determined from the original state at the beginning
   * of the transaction/commit instead of the "current" intermediate state.**
   *
   * @param {(string|number|undefined)} key a string or a number.
   * @return {ObjectData} null if the key is not present in the model,
   * or if the key is null or undefined or not a string or number.
   * @see #containsLinkData
   * @see #getKeyForLinkData
   * @since 1.6
   */
  findLinkDataForKey(key: go.Key): LinkData | null;
}

export interface ModelSnapshot {
  class: 'GraphLinksModel';
  linkDataArray: LinkData[];
  linkKeyProperty: 'key';
  modelData: ModelData;
  nodeDataArray: NodeData[];
}

export const createEmptyModelSnapshot = (): ModelSnapshot => ({
  class: 'GraphLinksModel',
  linkDataArray: [],
  linkKeyProperty: 'key',
  modelData: createEmptyModelData(),
  nodeDataArray: [],
});
